import { useState } from "react";
import { Search, Mail, ChevronRight, ChevronDown } from "lucide-react";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import HelpContactModal from "@/components/public/HelpContactModal";
import HelpArticlesSkeleton from "@/components/public/skeletons/HelpArticlesSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/common.utils";
import { useGetHelpArticles } from "@/hooks/useHelp";

export default function HelpCenter() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  // Search and the category facet are real query params — the box used to have
  // no `onChange` at all.
  const { data, isLoading } = useGetHelpArticles({
    search: search.trim() || undefined,
    category: category || undefined,
  });
  const articles = data?.articles ?? [];
  const categories = data?.filters.categories ?? [];

  return (
    <div className="font-dashboard" data-testid="help-center-page">
      <div className="relative overflow-hidden rounded-2xl bg-dash-sidebarDark px-4 py-10 text-center sm:px-6 sm:py-12">
        <div className="pointer-events-none absolute -left-10 -top-10 size-28 rounded-full bg-white/5 sm:size-40" />
        <div className="pointer-events-none absolute -bottom-16 -left-6 size-32 rounded-full bg-white/5 sm:size-48" />
        <div className="pointer-events-none absolute -right-10 -top-16 size-32 rounded-full bg-white/5 sm:size-48" />
        <div className="pointer-events-none absolute -bottom-10 -right-10 size-28 rounded-full bg-white/5 sm:size-40" />

        <h1 className="relative text-2xl font-bold text-white sm:text-3xl">How can we help you?</h1>
        <p className="relative mt-2 text-sm text-white/60">Search our docs, or browse by category below.</p>

        <div className="relative mx-auto mt-6 max-w-md">
          <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-white/40" />
          <Input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search articles…"
            className="h-11 rounded-xl border-white/10 bg-white/10 pl-11 text-white placeholder:text-white/40 focus-visible:ring-white/30"
            data-testid="help-center-search"
          />
        </div>

        {isLoading && categories.length === 0 && (
          <div className="relative mx-auto mt-5 flex flex-wrap items-center justify-center gap-2" role="status" aria-label="Loading categories">
            {[56, 112, 116, 132, 116, 104].map((width, index) => (
              <Skeleton key={index} className="h-[30px] rounded-full bg-white/10 before:via-white/20" style={{ width }} />
            ))}
          </div>
        )}

        {categories.length > 0 && (
          <div className="relative mx-auto mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              variant="seamless"
              onClick={() => setCategory("")}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                category === "" ? "bg-white text-dash-ink" : "bg-white/10 text-white/70 hover:bg-white/20",
              )}
              data-testid="help-center-category-all"
            >
              All
            </Button>
            {categories.map((item) => (
              <Button
                key={item}
                type="button"
                variant="seamless"
                onClick={() => setCategory(item)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  category === item ? "bg-white text-dash-ink" : "bg-white/10 text-white/70 hover:bg-white/20",
                )}
                data-testid={`help-center-category-${item}`}
              >
                {item}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-3 mt-8 flex items-center gap-3">
        <h2 className="text-sm font-semibold text-dash-ink">
          {search.trim() || category ? "Matching articles" : "Popular articles"}
        </h2>
        <div className="h-px flex-1 bg-dash-border" />
      </div>

      {isLoading ? (
        <HelpArticlesSkeleton />
      ) : articles.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No articles found"
          description="Try a different search term, or email us and a person will answer."
        />
      ) : (
        <SectionCard contentClassName="p-0" data-testid="help-center-articles">
          {articles.map((article, index) => {
            const open = openId === article.id;
            return (
              <div key={article.id} className={index > 0 ? "border-t border-dash-border" : undefined}>
                <Button
                  variant="seamless"
                  type="button"
                  onClick={() => setOpenId(open ? null : article.id)}
                  aria-expanded={open}
                  className="group flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-dash-hover sm:px-5"
                  data-testid={`help-center-article-${index}`}
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-dash-hover text-xs font-semibold text-dash-greenMid transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-dash-ink">{article.question}</span>
                    <span className="block truncate text-xs text-dash-faint">{article.category}</span>
                  </span>
                  {open
                    ? <ChevronDown className="size-4 shrink-0 text-dash-muted" />
                    : <ChevronRight className="size-4 shrink-0 text-dash-muted" />}
                </Button>
                {open && (
                  <div className="px-4 pb-5 pl-[3.4rem] pr-6 text-sm leading-6 text-dash-muted sm:px-5 sm:pl-[4rem]">
                    {article.answer}
                  </div>
                )}
              </div>
            );
          })}
        </SectionCard>
      )}

      <div className="mt-4 flex min-h-[126px] flex-col justify-between gap-5 rounded-[17px] border border-dash-border bg-white px-5 py-[30px] sm:flex-row sm:items-center">
        <div className="min-w-0">
          <Mail className="mb-1.5 size-7 text-dash-muted" strokeWidth={1.25} />
          <div className="text-sm font-semibold leading-5 text-dash-ink">Email support</div>
          <div className="mt-0.5 text-xs leading-5 text-dash-faint">
            Send us a message and we&apos;ll get back within one business day.
          </div>
        </div>
        <Button
          type="button"
          variant="dashboardPrimary"
          size="sm"
          className="w-full shrink-0 sm:w-[117px]"
          onClick={() => setContactOpen(true)}
          data-testid="help-center-send-email"
        >
          Send email
        </Button>
      </div>

      <div className="mt-4 flex justify-center gap-4 text-xs text-dash-faint">
        <a href="https://www.yourstoop.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-dash-ink hover:underline" data-testid="help-center-privacy-link">
          Privacy Policy
        </a>
        <span aria-hidden="true">&middot;</span>
        <a href="https://www.yourstoop.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="hover:text-dash-ink hover:underline" data-testid="help-center-terms-link">
          Terms &amp; Conditions
        </a>
      </div>

      {/* Reuses the existing contact endpoint rather than a second one that
          would write to the same place under a different name. */}
      <HelpContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
